import {
    IBankDetails,
    IFinancialDetailsData,
    IFinancialDetailsSubmitRequest,
    IGSTDetails,
    IPANDetails,
} from 'utils/api/types';
import {
    gstPanValidation,
    gstValidation,
    panValidation,
} from 'utils/validation';
import { autoLobPartners, autoPartnershipPartners } from 'utils/constants';
import {
    FinancialFormValues,
    IDocumentUploadState,
    IFinancialFieldConfig,
    IFinancialDropdownOption,
    getLegalDocumentStorageKey,
    LEGACY_LOCAL_STORAGE_KEY,
    LOCAL_STORAGE_KEY_PREFIX,
} from './types';

export function getLobPrefix(
    partnerId: string,
    permissions?: { lob?: string; permission_list?: string[] }
): string {
    if (permissions?.lob) {
        return permissions.lob;
    }
    if (
        autoLobPartners.includes(partnerId) ||
        permissions?.permission_list?.includes(
            'access_only_auto_commercial_flow'
        )
    ) {
        return 'auto-lob';
    }
    if (autoPartnershipPartners.includes(partnerId)) {
        return 'auto-partnership';
    }
    return 'embedded';
}

export function flattenFinancialData(
    data?: IFinancialDetailsData | null,
    uiConfig: IFinancialFieldConfig[] = [],
    partnerName?: string | null
): FinancialFormValues {
    const values: FinancialFormValues = {};
    const bank = data?.bank_details || {};
    const gst = data?.gst_details;
    const pan = data?.pan_details;
    const hasAnyPersistedData = !!(
        data?.bank_details ||
        data?.gst_details ||
        data?.pan_details
    );
    const nameSoT = (partnerName || '').trim();

    values.dealership_name = nameSoT || gst?.dealership_name || '';
    values.gst_type = gst?.type
        ? {
              value: gst.type,
              displayValue: capitalize(gst.type),
          }
        : { value: '', displayValue: '' };
    values.gst_number = gst?.number || '';
    values.pan_number = pan?.number || '';
    values.pan_image_url = toUploadState(pan?.image_url);
    values.bank_name = bank.bank_name || '';
    values.beneficiary_name = nameSoT || bank.beneficiary_name || '';
    values.ifsc_code = bank.ifsc_code || '';
    values.account_number = bank.account_number || '';
    values.confirm_account_number = bank.account_number || '';
    values.cancelled_cheque_image_url = toUploadState(
        bank.cancelled_cheque_image_url
    );
    values.tnc = false;

    uiConfig.forEach((field) => {
        const key = field.props?.key_name;
        if (!key) return;
        if (field.props?.default !== undefined && values[key] === undefined) {
            values[key] = field.props.default;
        }
        if (field.component === 'dropdown' && typeof values[key] === 'string') {
            values[key] = optionFromValue(field.props?.options, values[key]);
        }
        if (
            field.component === 'document_upload' &&
            typeof values[key] === 'string'
        ) {
            values[key] = toUploadState(values[key]);
        }
    });

    if (hasAnyPersistedData) {
        values.gst_applicable = !!gst;
    } else if (values.gst_applicable === undefined) {
        const gstField = uiConfig.find(
            (field) => field.props?.key_name === 'gst_applicable'
        );
        values.gst_applicable =
            gstField?.props?.default !== undefined
                ? gstField.props.default
                : true;
    }

    return values;
}

function capitalize(value: string) {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function optionFromValue(
    options: IFinancialDropdownOption[] | undefined,
    value: string
) {
    const match = options?.find((opt) => opt.value === value);
    return {
        value,
        displayValue:
            match?.display_value || match?.displayValue || capitalize(value),
    };
}

function toUploadState(url?: string | null): IDocumentUploadState {
    if (!url) {
        return { name: '', uploadStatus: '', url: '' };
    }
    return {
        name: url.split('/').pop() || 'document',
        uploadStatus: 'uploaded',
        url,
    };
}

export function isFieldVisible(
    field: IFinancialFieldConfig,
    values: FinancialFormValues
): boolean {
    const visibleWhen = field.props?.visible_when;
    if (!visibleWhen) return true;
    return Object.entries(visibleWhen).every(([key, expected]) => {
        const current = values[key];
        if (typeof expected === 'boolean') {
            return Boolean(current) === expected;
        }
        if (typeof current === 'object' && current?.value !== undefined) {
            return current.value === expected;
        }
        return current === expected;
    });
}

/** Name SoT fields set `editable` explicitly; other fields default to editable. */
export function isFieldEditable(field: IFinancialFieldConfig): boolean {
    if (!field.props || !('editable' in field.props)) {
        return true;
    }
    return field.props.editable === true;
}

export function applySyncGroupValue(
    uiConfig: IFinancialFieldConfig[],
    values: FinancialFormValues,
    changedKey: string,
    nextValue: any
): FinancialFormValues {
    const changedField = uiConfig.find((f) => f.props?.key_name === changedKey);
    const syncGroup = changedField?.props?.sync_group;
    if (!syncGroup) {
        return { ...values, [changedKey]: nextValue };
    }
    const next = { ...values, [changedKey]: nextValue };
    uiConfig.forEach((field) => {
        if (
            field.props?.sync_group === syncGroup &&
            field.props.key_name !== changedKey
        ) {
            next[field.props.key_name] = nextValue;
        }
    });
    return next;
}

export function getFieldValue(
    field: IFinancialFieldConfig,
    values: FinancialFormValues
): any {
    return values[field.props.key_name];
}

export function getDisplayValue(
    field: IFinancialFieldConfig,
    values: FinancialFormValues
): string {
    const value = getFieldValue(field, values);
    if (field.component === 'yes_no') {
        return value ? 'Yes' : 'No';
    }
    if (field.component === 'dropdown') {
        return (
            value?.displayValue || value?.display_value || value?.value || ''
        );
    }
    if (field.component === 'document_upload') {
        return value?.url ? 'Uploaded' : 'Not uploaded';
    }
    if (field.component === 'tnc_checkbox') {
        return value ? 'Accepted' : 'Not accepted';
    }
    return value == null ? '' : String(value);
}

export function validateField(
    field: IFinancialFieldConfig,
    values: FinancialFormValues
): string {
    if (!isFieldVisible(field, values)) return '';

    const key = field.props.key_name;
    const value = values[key];
    const validators = field.props.validators || [];

    if (field.required) {
        if (field.component === 'document_upload') {
            if (!value?.url || value.uploadStatus !== 'uploaded') {
                return `Please upload ${field.label}`;
            }
        } else if (field.component === 'dropdown') {
            if (!value?.value) {
                return `Please select ${field.label}`;
            }
        } else if (field.component === 'yes_no') {
            // boolean always present
        } else if (field.component === 'tnc_checkbox') {
            if (!value) {
                return 'Please accept Terms and Conditions';
            }
        } else if (!value || String(value).trim().length === 0) {
            return `Please enter ${field.label}`;
        }
    }

    const validatorError = validators
        .map((validator) => {
            if (validator === 'gst') {
                if (!gstValidation(String(value || ''))) {
                    return 'Please enter a valid GST number';
                }
            }
            if (validator === 'pan') {
                if (!panValidation(String(value || ''))) {
                    return 'Please enter a valid PAN number';
                }
            }
            if (validator === 'gst_pan_match') {
                const gstApplicable = Boolean(values.gst_applicable);
                if (
                    !gstPanValidation(
                        String(values.gst_number || ''),
                        String(value || ''),
                        gstApplicable
                    )
                ) {
                    return 'Please enter a valid PAN number';
                }
            }
            if (validator.startsWith('min_length:')) {
                const min = Number(validator.split(':')[1]);
                if (String(value || '').length < min) {
                    return `Please enter a valid ${field.label.toLowerCase()}`;
                }
            }
            if (validator.startsWith('matches:')) {
                const otherKey = validator.split(':')[1];
                if (String(value || '') !== String(values[otherKey] || '')) {
                    return 'Account number does not match. Please check the entered account number';
                }
            }
            if (validator === 'ifsc') {
                if (!value || String(value).length < 5) {
                    return 'Please enter a valid IFSC code';
                }
            }
            return '';
        })
        .find((message) => message.length > 0);

    return validatorError || '';
}

export function validateAllFields(
    uiConfig: IFinancialFieldConfig[],
    values: FinancialFormValues,
    options?: { skipIfscRemote?: boolean; isValidIfsc?: boolean }
): { valid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};
    let valid = true;

    uiConfig.forEach((field) => {
        if (!isFieldVisible(field, values)) return;
        const error = validateField(field, values);
        if (error) {
            errors[field.props.key_name] = error;
            valid = false;
        } else {
            errors[field.props.key_name] = '';
        }
        if (
            field.props.validators?.includes('ifsc') &&
            !options?.skipIfscRemote &&
            options?.isValidIfsc === false
        ) {
            errors[field.props.key_name] = 'Please enter a valid IFSC code';
            valid = false;
        }
    });

    return { valid, errors };
}

export function hasAllRequiredValues(
    uiConfig: IFinancialFieldConfig[],
    values: FinancialFormValues,
    isValidIfsc: boolean
): boolean {
    const { valid } = validateAllFields(uiConfig, values, {
        isValidIfsc,
    });
    return valid;
}

export function buildSubmitPayload(
    uiConfig: IFinancialFieldConfig[],
    values: FinancialFormValues,
    options?: { includeNameFields?: boolean }
): IFinancialDetailsSubmitRequest {
    const bankDetails: IBankDetails = {};
    let panDetails: IPANDetails = { number: '', image_url: '' };
    let gstDetails: IGSTDetails | undefined;
    const gstApplicable = Boolean(values.gst_applicable);

    const shouldIncludeField = (field: IFinancialFieldConfig) => {
        if (!isFieldVisible(field, values)) return false;
        if (
            (field.props.key_name === 'dealership_name' ||
                field.props.key_name === 'beneficiary_name') &&
            (isFieldEditable(field) || options?.includeNameFields)
        ) {
            return true;
        }
        return field.props?.submit !== false;
    };

    uiConfig.forEach((field) => {
        if (!shouldIncludeField(field)) return;

        const key = field.props.key_name;
        const raw = values[key];

        switch (key) {
            case 'bank_name':
                bankDetails.bank_name = String(raw || '');
                break;
            case 'beneficiary_name':
                bankDetails.beneficiary_name = String(raw || '');
                break;
            case 'ifsc_code':
                bankDetails.ifsc_code = String(raw || '');
                break;
            case 'account_number':
                bankDetails.account_number = String(raw || '');
                break;
            case 'cancelled_cheque_image_url':
            case 'cancelled_cheque':
                bankDetails.cancelled_cheque_image_url = raw?.url || '';
                break;
            case 'pan_number':
                panDetails = {
                    ...panDetails,
                    number: String(raw || ''),
                };
                break;
            case 'pan_image_url':
            case 'pan_image':
                panDetails = {
                    ...panDetails,
                    image_url: raw?.url || '',
                };
                break;
            case 'dealership_name':
                gstDetails = {
                    ...(gstDetails || {
                        type: '',
                        number: '',
                        dealership_name: '',
                    }),
                    dealership_name: String(raw || ''),
                };
                break;
            case 'gst_type':
                if (gstApplicable) {
                    gstDetails = {
                        ...(gstDetails || {
                            type: '',
                            number: '',
                            dealership_name: '',
                        }),
                        type: raw?.value || String(raw || ''),
                    };
                }
                break;
            case 'gst_number':
                if (gstApplicable) {
                    gstDetails = {
                        ...(gstDetails || {
                            type: '',
                            number: '',
                            dealership_name: '',
                        }),
                        number: String(raw || ''),
                    };
                }
                break;
            default:
                break;
        }
    });

    // dealership_name may live in identity section while gst is optional —
    // include dealership on gst when gst applicable even if only set once
    if (gstApplicable) {
        gstDetails = {
            dealership_name: String(
                values.dealership_name || gstDetails?.dealership_name || ''
            ),
            type: String(values.gst_type?.value || gstDetails?.type || ''),
            number: String(values.gst_number || gstDetails?.number || ''),
        };
    }

    const payload: IFinancialDetailsSubmitRequest = {
        bank_details: bankDetails,
        pan_details: panDetails,
    };
    if (gstDetails && (gstApplicable || gstDetails.dealership_name)) {
        payload.gst_details = gstDetails;
    }
    return payload;
}

export function groupFieldsBySection(
    uiConfig: IFinancialFieldConfig[],
    values: FinancialFormValues
): Array<{ section: string; fields: IFinancialFieldConfig[] }> {
    const order: string[] = [];
    const map: Record<string, IFinancialFieldConfig[]> = {};

    uiConfig.forEach((field) => {
        if (!isFieldVisible(field, values)) return;
        if (field.component === 'tnc_checkbox') return;
        const section = field.props?.section || 'identity';
        if (!map[section]) {
            map[section] = [];
            order.push(section);
        }
        map[section].push(field);
    });

    return order.map((section) => ({
        section,
        fields: map[section],
    }));
}

export function loadDraftFromStorage(
    partnerId: string,
    groupName: string
): FinancialFormValues | null {
    if (!partnerId || !groupName) return null;
    try {
        const key = getLegalDocumentStorageKey(partnerId, groupName);
        let raw = localStorage.getItem(key);
        // One-time migrate from unscoped legacy key into this partner/group slot
        if (!raw) {
            const legacy = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
            if (legacy && legacy !== '{}') {
                localStorage.setItem(key, legacy);
                localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
                raw = legacy;
            }
        }
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || JSON.stringify(parsed) === '{}') return null;
        return parsed;
    } catch {
        return null;
    }
}

export function saveDraftToStorage(
    partnerId: string,
    groupName: string,
    values: FinancialFormValues
) {
    if (!partnerId || !groupName) return;
    try {
        localStorage.setItem(
            getLegalDocumentStorageKey(partnerId, groupName),
            JSON.stringify(values)
        );
    } catch {
        // ignore quota / private mode
    }
}

export function clearDraftStorage(partnerId: string, groupName: string) {
    if (!partnerId || !groupName) return;
    try {
        localStorage.removeItem(
            getLegalDocumentStorageKey(partnerId, groupName)
        );
        // Drop legacy key if still present so it cannot leak into another group
        localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
    } catch {
        // ignore
    }
}

/** Clears every legal-documents draft (scoped + legacy). Use on logout. */
export function clearAllLegalDocumentDrafts() {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (
                key &&
                (key === LEGACY_LOCAL_STORAGE_KEY ||
                    key.startsWith(LOCAL_STORAGE_KEY_PREFIX))
            ) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
        // ignore
    }
}

export function mergeDraftValues(
    base: FinancialFormValues,
    draft: FinancialFormValues | null
): FinancialFormValues {
    if (!draft) return base;
    return {
        ...base,
        ...draft,
        pan_image_url: draft.pan_image_url || base.pan_image_url,
        cancelled_cheque_image_url:
            draft.cancelled_cheque_image_url || base.cancelled_cheque_image_url,
        gst_type: draft.gst_type || base.gst_type,
    };
}

export function mapDropdownOptions(options: IFinancialDropdownOption[] = []) {
    return options.map((opt) => ({
        value: opt.value,
        displayValue: opt.display_value || opt.displayValue || opt.value,
    }));
}
